// Tabellenansicht der Rechnungen ab 1200 px (design_handoff_rechnungen §4): Raster statt <table>, Spaltenfilter
// Zuständigkeit/Status, Sortierung Datum/Betrag/Fälligkeit, Zellen-Dropdowns „Verantwortung“ und Status (inkl.
// Klärungs-Wiedervorlage), Chips am Beleg, Gruppenköpfe, Rechtsklick und Dokument-Vorschau. Alle Aktionen und
// Zustände kommen aus components/InvoicesView.tsx — hier nur Darstellung.
import type { MouseEvent as ReactMouseEvent, ReactNode, RefObject } from 'react';
import {
  ArrowBendUpRight, ArrowLineDown, ArrowLineUp, ArrowSquareOut, Buildings, CaretDown, CaretUp, Check, CircleNotch, GlobeSimple,
  HouseLine, ListBullets, User, UsersThree, X,
} from '@phosphor-icons/react';
import type { Rechnung } from '../../../../components/InvoicesView';
import type { ObjektGruppe } from '../../../../lib/rechnungen/objektFilter';
import { getFaelligkeitsBadge, getRichtung, RICHTUNG_LABEL } from '../../../../lib/rechnungen/sichtbarkeit';
import { KLAERUNG_PRESETS } from '../../../../lib/rechnungen/klaerung';
import { AuswahlKaestchen } from '../../../../components/ui/AuswahlKaestchen';
import {
  belegChips, belegSpruenge, betragFarbe, CHIP_FARBE, eur, statusText, statusTon,
} from '../../../../lib/rechnungenAnsicht/darstellung';

export type SortFeld = 'datum' | 'betrag' | 'faelligkeit';
export type AnzeigeEintrag = { typ: 'gruppe'; gruppe: ObjektGruppe<Rechnung> } | { typ: 'zeile'; row: Rechnung };

interface Person { id: string; full_name: string }
interface Team { id: string; name: string }
type Partner = ReadonlyMap<string, { id: string; nummer?: string }>;

export interface RechnungsTabelleProps {
  eintraege: readonly AnzeigeEintrag[];
  /** Detail-Seitenleiste offen: Firma/Zuständigkeit ausblenden (wie bisher) */
  kompakt: boolean;
  // Auswahl
  ausgewaehlt: ReadonlySet<string>; alleSichtbarenGewaehlt: boolean; auswahlMoeglich: boolean;
  onAuswahl: (id: string) => void; onAlleSichtbaren: () => void;
  // Sortierung
  sortFeld: SortFeld; sortRichtung: 'asc' | 'desc'; onSort: (f: SortFeld) => void;
  // Spaltenfilter
  offenerSpaltenfilter: string | null; setOffenerSpaltenfilter: (s: string | null) => void;
  filterZustaendig: string; setFilterZustaendig: (z: string) => void;
  filterStatus: string; setFilterStatus: (s: string) => void;
  mitglieder: readonly Person[]; teams: readonly Team[];
  // Zellen-Dropdowns
  offeneZustaendigkeit: string | null; setOffeneZustaendigkeit: (id: string | null) => void; zustaendigRef: RefObject<HTMLDivElement | null>;
  aktualisiertZustaendigkeit: string | null; onZustaendigkeit: (r: Rechnung, id: string | null, typ: 'mitarbeiter' | 'team' | null) => void;
  offenerStatus: string | null; setOffenerStatus: (id: string | null) => void; statusRef: RefObject<HTMLDivElement | null>;
  aktualisiertStatus: string | null; onStatus: (r: Rechnung, s: string) => void;
  onWiedervorlageTage: (r: Rechnung, tage: number) => void; onWiedervorlageDatum: (r: Rechnung, datum: string) => void;
  // Gruppen
  offeneGruppen: ReadonlySet<string>; onGruppe: (key: string) => void; nachHaus: boolean;
  // Zeile
  hausName: (id: string | null | undefined) => string;
  partner: { nachfolger: Partner; vorgaenger: Partner };
  mitAdresse: boolean;
  markiertId: string | null;
  onOeffnen: (id: string) => void;
  /** Sprung zur Korrektur-Partnerin (eigener Weg, nicht über den Zeilen-Klick-Merker) */
  onSprung: (id: string) => void;
  onZeileMausDruck: (e: ReactMouseEvent) => void;
  onKontextmenue: (e: ReactMouseEvent, r: Rechnung) => void;
  // Vorschau
  vorschauId: string | null; onVorschauSchliessen: () => void; driveId: (url: string | null | undefined) => string | null;
}

const SPALTEN = '28px minmax(60px,0.6fr) minmax(200px,2.2fr) minmax(120px,1.3fr) minmax(140px,1fr) minmax(100px,0.8fr) minmax(160px,0.9fr)';
const SPALTEN_KOMPAKT = '28px minmax(60px,0.6fr) minmax(200px,2.2fr) minmax(100px,0.8fr) minmax(160px,0.9fr)';

/** Initialen für den Zuständigkeits-Avatar (max. 2 Buchstaben) — wie bisher. */
const initialen = (name?: string | null): string => {
  if (!name) return '';
  const teile = name.trim().split(/\s+/).filter(Boolean);
  if (teile.length === 0) return '';
  if (teile.length === 1) return teile[0].slice(0, 2).toUpperCase();
  return (teile[0][0] + teile[teile.length - 1][0]).toUpperCase();
};

function Menue({ children, ausrichtung = 'links', breite, oben = 38, refObj, label }: {
  children: ReactNode; ausrichtung?: 'links' | 'rechts'; breite: number; oben?: number; refObj?: RefObject<HTMLDivElement | null>; label?: string;
}) {
  return (
    <div ref={refObj} role="menu" aria-label={label} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}
      className="immo-pop-in absolute flex flex-col text-left"
      style={{ top: oben, [ausrichtung === 'links' ? 'left' : 'right']: 0, width: breite, zIndex: 40, borderRadius: 18, padding: 6, gap: 2,
        background: 'var(--sheet)', color: 'var(--ink)', boxShadow: '0 24px 50px -16px var(--shade), inset 0 0 0 1px var(--line)' }}>
      {children}
    </div>
  );
}

function MenueZeile({ onClick, children, aktiv = false, haken = false }: { onClick: () => void; children: ReactNode; aktiv?: boolean; haken?: boolean }) {
  return (
    <button type="button" role="menuitem" onClick={onClick} className="immo-zeile-hover flex items-center"
      style={{ gap: 8, minHeight: 36, padding: '8px 10px', border: 'none', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
        textAlign: 'left', color: 'var(--ink)', background: aktiv ? 'var(--acc-soft)' : 'transparent' }}>
      <span className="flex min-w-0 flex-1 items-center" style={{ gap: 8 }}>{children}</span>
      {haken && <Check size={12} weight="bold" color="var(--acc)" className="shrink-0" />}
    </button>
  );
}

const STATUS_SPALTENFILTER: [string, string][] = [
  ['alle', 'Alle'], ['offen', 'Offen'], ['bezahlt', 'Bezahlt'], ['unklar', 'Unklar'], ['overdue', 'Überfällig'], ['zur_pruefung', 'Zur Prüfung'], ['zur_freigabe', 'Zur Freigabe'],
];

export function RechnungsTabelle(p: RechnungsTabelleProps) {
  const spalten = p.kompakt ? SPALTEN_KOMPAKT : SPALTEN;
  const sortPfeil = (f: SortFeld) => (p.sortFeld !== f ? null : p.sortRichtung === 'asc'
    ? <CaretUp size={11} weight="bold" color="var(--acc)" /> : <CaretDown size={11} weight="bold" color="var(--acc)" />);
  const kopfKnopf = { display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: 'inherit',
    fontSize: 11.5, fontWeight: 800, color: 'var(--ink3)' } as const;

  return (
    <div role="table" aria-label="Rechnungen" className="immo-glas" style={{ borderRadius: 26, padding: 8 }}>
      {/* Kopfzeile */}
      <div role="row" className="grid items-center" style={{ gridTemplateColumns: spalten, gap: 12, padding: '10px 14px', fontSize: 11.5, fontWeight: 800, color: 'var(--ink3)' }}>
        <span role="columnheader">
          <AuswahlKaestchen an={p.alleSichtbarenGewaehlt} onUmschalten={p.onAlleSichtbaren} gesperrt={!p.auswahlMoeglich}
            label="Alle sichtbaren auswählen" tip={p.auswahlMoeglich ? 'Alle sichtbaren auswählen' : undefined} />
        </span>
        <span role="columnheader">Objekt</span>
        <span role="columnheader">
          <button type="button" onClick={() => p.onSort('datum')} style={kopfKnopf} data-tip="Nach Datum sortieren">Beleg {sortPfeil('datum')}</button>
        </span>
        {!p.kompakt && <span role="columnheader">Firma</span>}
        {!p.kompakt && (
          <span role="columnheader" className="relative" data-spaltenfilter>
            <button type="button" onClick={() => p.setOffenerSpaltenfilter(p.offenerSpaltenfilter === 'zustaendig' ? null : 'zustaendig')} data-tip="Zuständigkeit filtern"
              aria-expanded={p.offenerSpaltenfilter === 'zustaendig'} style={{ ...kopfKnopf, color: p.filterZustaendig !== 'alle' ? 'var(--acc)' : 'var(--ink3)' }}>
              Zuständigkeit <CaretDown size={10} weight="bold" />
            </button>
            {p.offenerSpaltenfilter === 'zustaendig' && (
              <Menue breite={220} oben={26} label="Zuständigkeit filtern">
                <MenueZeile aktiv={p.filterZustaendig === 'alle'} onClick={() => { p.setFilterZustaendig('alle'); p.setOffenerSpaltenfilter(null); }}><ListBullets size={13} weight="bold" color="var(--acc)" />Alle</MenueZeile>
                <MenueZeile aktiv={p.filterZustaendig === 'global'} onClick={() => { p.setFilterZustaendig('global'); p.setOffenerSpaltenfilter(null); }}><GlobeSimple size={13} weight="bold" color="var(--acc)" />Global</MenueZeile>
                {p.mitglieder.map((m) => (
                  <MenueZeile key={m.id} aktiv={p.filterZustaendig === m.id} onClick={() => { p.setFilterZustaendig(m.id); p.setOffenerSpaltenfilter(null); }}><User size={13} weight="bold" color="var(--acc)" />{m.full_name}</MenueZeile>
                ))}
                {p.teams.map((t) => (
                  <MenueZeile key={t.id} aktiv={p.filterZustaendig === 't:' + t.id} onClick={() => { p.setFilterZustaendig('t:' + t.id); p.setOffenerSpaltenfilter(null); }}><UsersThree size={13} weight="bold" color="var(--acc)" />{t.name}</MenueZeile>
                ))}
              </Menue>
            )}
          </span>
        )}
        <span role="columnheader" className="flex justify-end">
          <button type="button" onClick={() => p.onSort('betrag')} style={kopfKnopf} data-tip="Nach Betrag sortieren">Betrag {sortPfeil('betrag')}</button>
        </span>
        <span role="columnheader" className="relative flex items-center justify-center" style={{ gap: 6 }} data-spaltenfilter>
          <button type="button" onClick={() => p.onSort('faelligkeit')} data-tip="Nach Fälligkeit sortieren"
            style={{ ...kopfKnopf, color: p.filterStatus !== 'alle' ? 'var(--acc)' : 'var(--ink3)' }}>Status {sortPfeil('faelligkeit')}</button>
          <button type="button" onClick={() => p.setOffenerSpaltenfilter(p.offenerSpaltenfilter === 'status' ? null : 'status')} data-tip="Status filtern" aria-label="Status filtern"
            aria-expanded={p.offenerSpaltenfilter === 'status'} className="inline-flex items-center justify-center"
            style={{ width: 20, height: 20, borderRadius: 6, border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink2)' }}>
            <CaretDown size={10} weight="bold" />
          </button>
          {p.offenerSpaltenfilter === 'status' && (
            <Menue breite={180} oben={26} ausrichtung="rechts" label="Status filtern">
              {STATUS_SPALTENFILTER.map(([k, l]) => (
                <MenueZeile key={k} aktiv={p.filterStatus === k} onClick={() => { p.setFilterStatus(k); p.setOffenerSpaltenfilter(null); }}>{l}</MenueZeile>
              ))}
            </Menue>
          )}
        </span>
      </div>

      {p.eintraege.map((e) => {
        if (e.typ === 'gruppe') {
          const g = e.gruppe;
          const auf = p.offeneGruppen.has(g.key);
          const I = p.nachHaus ? HouseLine : Buildings;
          return (
            <button key={`gruppe-${g.key}`} type="button" onClick={() => p.onGruppe(g.key)} aria-expanded={auf}
              className="flex w-full items-center text-left"
              style={{ gap: 10, marginTop: 4, padding: '12px 14px', borderRadius: 16, border: 'none', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--ink)', background: 'var(--fill)' }}>
              <CaretDown size={14} weight="bold" color="var(--ink3)" className="shrink-0" style={{ transition: 'transform .25s', transform: auf ? 'none' : 'rotate(-90deg)' }} />
              <I size={16} weight="fill" color="var(--acc)" className="shrink-0" />
              <span className="min-w-0 flex-1 truncate" style={{ fontSize: 14, fontWeight: 800 }}>{g.label}</span>
              <span className="shrink-0" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{g.summe.anzahl} {g.summe.anzahl === 1 ? 'Beleg' : 'Belege'}</span>
              <span className="shrink-0 tabular-nums" style={{ fontSize: 13, fontWeight: 800 }}>{eur(g.summe.brutto)}</span>
              <span className="shrink-0 tabular-nums" style={{ fontSize: 12, fontWeight: 700, color: 'var(--warn)' }}>offen {eur(g.summe.offen)}</span>
            </button>
          );
        }
        return <Zeile key={e.row.id} p={p} row={e.row} spalten={spalten} />;
      })}
    </div>
  );
}

function Zeile({ p, row, spalten }: { p: RechnungsTabelleProps; row: Rechnung; spalten: string }) {
  const gewaehlt = p.ausgewaehlt.has(row.id);
  const markiert = p.markiertId === row.id || p.vorschauId === row.id;
  const richtung = getRichtung(row);
  const RichtungIcon = richtung === 'ausgang' ? ArrowLineUp : ArrowLineDown;
  const chips = belegChips(row);
  const spruenge = belegSpruenge(row, p.partner);
  const haus = p.hausName(row.haus_id);
  const fb = getFaelligkeitsBadge(row.faelligkeitsdatum, row.status);
  const ton = statusTon(row.status);
  const istKlaerung = row.status?.toLowerCase() === 'klaerung';
  const zustOffen = p.offeneZustaendigkeit === row.id;
  const statusOffen = p.offenerStatus === row.id;

  return (
    <>
      <div role="row" data-rechnung-row tabIndex={0}
        onMouseDown={p.onZeileMausDruck}
        onClick={() => p.onOeffnen(row.id)}
        onKeyDown={(e) => { if (e.target === e.currentTarget && e.key === 'Enter') p.onOeffnen(row.id); }}
        onContextMenu={(e) => p.onKontextmenue(e, row)}
        className="immo-zeile-hover grid cursor-pointer items-center"
        style={{ gridTemplateColumns: spalten, gap: 12, padding: '12px 14px', borderRadius: 16, borderTop: '1px solid var(--line)',
          background: gewaehlt ? 'var(--acc-soft)' : markiert ? 'var(--fill)' : 'transparent', boxShadow: markiert ? 'inset 3px 0 0 var(--acc)' : undefined }}>
        <span role="cell"><AuswahlKaestchen an={gewaehlt} onUmschalten={() => p.onAuswahl(row.id)} label={`${row.rechnungsnummer} auswählen`} /></span>

        <span role="cell" className="flex min-w-0 flex-col" style={{ gap: 2 }}>
          <span className="truncate" style={{ fontSize: 13, fontWeight: 800 }}>{row.objektnummer || row._matchedObjektnummer || '-'}</span>
          {haus && <span className="truncate" style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>{haus}</span>}
        </span>

        <span role="cell" className="flex min-w-0 flex-col" style={{ gap: 5 }}>
          <span className="flex min-w-0 items-center" style={{ gap: 6 }}>
            <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{row.rechnungsnummer}</span>
            <span data-tip={RICHTUNG_LABEL[richtung]} className="inline-flex shrink-0"><RichtungIcon size={13} weight="bold" color="var(--ink3)" aria-label={RICHTUNG_LABEL[richtung]} /></span>
          </span>
          {(chips.length > 0 || spruenge.length > 0) && (
            <span className="flex min-w-0 max-w-full flex-wrap" style={{ gap: 4 }}>
              {chips.map((c) => (
                <span key={c.key} data-tip={c.tip} className="block max-w-full truncate"
                  style={{ height: 20, lineHeight: '20px', padding: '0 8px', borderRadius: 10, fontSize: 10.5, fontWeight: 800, background: CHIP_FARBE[c.ton].flaeche, color: CHIP_FARBE[c.ton].farbe }}>
                  {c.label}
                </span>
              ))}
              {spruenge.map((s) => (
                <button key={s.key} type="button" disabled={!s.zielId} data-tip={s.tip || undefined}
                  onClick={(ev) => { ev.stopPropagation(); if (s.zielId) p.onSprung(s.zielId); }}
                  className="inline-flex max-w-full items-center"
                  style={{ height: 20, padding: '0 8px', gap: 3, borderRadius: 10, border: 'none', cursor: s.zielId ? 'pointer' : 'default', fontFamily: 'inherit',
                    fontSize: 10.5, fontWeight: 800, background: 'var(--fill3)', color: 'var(--ink)', opacity: s.zielId ? 1 : 0.5 }}>
                  <ArrowBendUpRight size={10} weight="bold" className="shrink-0" /><span className="truncate">{s.label}</span>
                </button>
              ))}
            </span>
          )}
          {p.mitAdresse && row.objekt_adresse && <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{row.objekt_adresse}</span>}
        </span>

        {!p.kompakt && (
          <span role="cell" className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{row.unternehmen}</span>
        )}

        {!p.kompakt && (
          <span role="cell" className="relative min-w-0" onClick={(ev) => ev.stopPropagation()}>
            <button type="button" onClick={() => p.setOffeneZustaendigkeit(zustOffen ? null : row.id)} data-tip="Zuständigkeit ändern" aria-expanded={zustOffen}
              className="inline-flex max-w-full items-center"
              style={{ height: 32, padding: '0 10px 0 4px', gap: 6, borderRadius: 16, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
                background: zustOffen ? 'var(--acc-soft)' : 'var(--fill2)', color: 'var(--ink)' }}>
              <span aria-hidden className="inline-flex shrink-0 items-center justify-center"
                style={{ width: 24, height: 24, borderRadius: '50%', fontSize: 9.5, fontWeight: 800,
                  background: row.profiles?.full_name ? 'var(--acc-fill)' : 'var(--fill3)', color: row.profiles?.full_name ? 'var(--acc-ink)' : 'var(--ink3)' }}>
                {p.aktualisiertZustaendigkeit === row.id ? <CircleNotch size={12} className="animate-spin" />
                  : row._isTeam ? <UsersThree size={12} weight="fill" /> : (row.profiles?.full_name ? initialen(row.profiles.full_name) : 'G')}
              </span>
              <span className="truncate">{row.profiles?.full_name || 'Global'}</span>
              <CaretDown size={10} weight="bold" color="var(--ink3)" className="shrink-0" />
            </button>
            {zustOffen && (
              <Menue refObj={p.zustaendigRef} breite={230} label="Verantwortung">
                <span style={{ padding: '6px 10px 4px', fontSize: 11, fontWeight: 800, color: 'var(--ink3)' }}>Verantwortung</span>
                <div className="immo-scroll flex flex-col" style={{ maxHeight: 240, overflowY: 'auto', gap: 2 }}>
                  <MenueZeile haken={!row.zustaendig_id} onClick={() => p.onZustaendigkeit(row, null, null)}><GlobeSimple size={13} weight="bold" color="var(--acc)" />Global</MenueZeile>
                  {p.mitglieder.map((m) => {
                    const aktiv = row.zustaendig_typ !== 'team' && row.zustaendig_id === m.id;
                    return <MenueZeile key={m.id} haken={aktiv} onClick={() => p.onZustaendigkeit(row, m.id, 'mitarbeiter')}><User size={13} weight="bold" color="var(--acc)" />{m.full_name}</MenueZeile>;
                  })}
                  {p.teams.map((t) => {
                    const aktiv = row.zustaendig_typ === 'team' && row.zustaendig_id === t.id;
                    return <MenueZeile key={t.id} haken={aktiv} onClick={() => p.onZustaendigkeit(row, t.id, 'team')}><UsersThree size={13} weight="bold" color="var(--acc)" />{t.name}</MenueZeile>;
                  })}
                </div>
              </Menue>
            )}
          </span>
        )}

        <span role="cell" className="whitespace-nowrap text-right tabular-nums" style={{ fontSize: 14, fontWeight: 800, color: betragFarbe(row) }}>
          {row.brutto.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
        </span>

        <span role="cell" className="relative flex flex-col items-center" style={{ gap: 4 }} onClick={(ev) => ev.stopPropagation()}>
          <button type="button" onClick={() => p.setOffenerStatus(statusOffen ? null : row.id)} data-tip="Status ändern" aria-expanded={statusOffen}
            className="inline-flex max-w-full items-center whitespace-nowrap"
            style={{ height: 28, padding: '0 10px', gap: 5, borderRadius: 14, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5, fontWeight: 800,
              background: ton.flaeche, color: ton.farbe }}>
            {p.aktualisiertStatus === row.id ? <CircleNotch size={11} className="animate-spin" /> : <span className="truncate">{statusText(row)}</span>}
            <CaretDown size={9} weight="bold" className="shrink-0" />
          </button>
          {fb && <span style={{ fontSize: 10.5, fontWeight: 800, color: fb === 'rot' ? 'var(--bad)' : 'var(--warn)' }}>{fb === 'rot' ? 'Überfällig' : 'Bald fällig'}</span>}
          {statusOffen && (
            <Menue refObj={p.statusRef} breite={210} oben={34} ausrichtung="rechts" label="Status ändern">
              <MenueZeile onClick={() => p.onStatus(row, 'offen')}><span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--warn)' }} />Offen</MenueZeile>
              <MenueZeile onClick={() => p.onStatus(row, 'bezahlt')}><span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ok)' }} />Bezahlt</MenueZeile>
              {istKlaerung && (
                <div className="flex flex-col" style={{ marginTop: 4, padding: '8px 10px 6px', gap: 6, borderTop: '1px solid var(--line)' }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink3)' }}>Wiedervorlage</span>
                  <div className="flex" style={{ gap: 4 }}>
                    {KLAERUNG_PRESETS.map((w) => (
                      <button key={w.tage} type="button" onClick={() => p.onWiedervorlageTage(row, w.tage)}
                        style={{ flex: 1, height: 30, borderRadius: 10, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5, fontWeight: 800, background: 'var(--info-soft)', color: 'var(--info)' }}>
                        {w.label}
                      </button>
                    ))}
                  </div>
                  <input type="date" min={new Date().toISOString().slice(0, 10)} aria-label="Wiedervorlage am"
                    onChange={(ev) => { if (ev.target.value) p.onWiedervorlageDatum(row, ev.target.value); }}
                    style={{ height: 32, padding: '0 8px', borderRadius: 10, border: 'none', outline: 'none', background: 'var(--fill2)', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: 'var(--ink)' }} />
                </div>
              )}
            </Menue>
          )}
        </span>
      </div>
      {p.vorschauId === row.id && <Vorschau row={row} driveId={p.driveId(row.datei_url)} onSchliessen={p.onVorschauSchliessen} />}
    </>
  );
}

/** Ausgeklappte Dokument-Vorschau unter der Zeile (Kontextmenü „Öffnen“): Drive-iframe, sonst Hinweis. */
export function Vorschau({ row, driveId, onSchliessen }: { row: Rechnung; driveId: string | null; onSchliessen: () => void }) {
  const knopf = { height: 30, padding: '0 12px', gap: 5, borderRadius: 15, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 800,
    background: 'var(--sheet)', color: 'var(--ink)', boxShadow: '0 6px 14px -6px var(--shade)' } as const;
  return (
    <div data-rechnung-preview style={{ padding: '6px 14px 14px' }} onClick={(e) => e.stopPropagation()}>
      <div className="relative overflow-hidden" style={{ borderRadius: 18, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
        {/* Steuerung oben links — Googles Vollbild-Symbol sitzt oben rechts. */}
        <div className="absolute flex" style={{ left: 10, top: 10, zIndex: 2, gap: 6 }}>
          {driveId && row.datei_url && (
            <button type="button" className="inline-flex items-center" style={knopf} onClick={() => window.open(row.datei_url!, '_blank')}>
              <ArrowSquareOut size={12} weight="bold" />Neuer Tab
            </button>
          )}
          {driveId && (
            <button type="button" className="inline-flex items-center" style={knopf} onClick={onSchliessen}><X size={11} weight="bold" />Schließen</button>
          )}
        </div>
        {driveId ? (
          <iframe src={`https://drive.google.com/file/d/${driveId}/preview`} className="block w-full" style={{ height: '75vh', border: 'none', background: '#fff' }}
            loading="lazy" title={`Vorschau ${row.rechnungsnummer || ''}`} />
        ) : (
          <div className="flex items-center justify-between" style={{ gap: 16, padding: '22px 18px' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink3)' }}>{row.datei_url ? 'Vorschau nur für Drive-Dokumente' : 'Kein Dokument hinterlegt'}</span>
            <button type="button" className="inline-flex items-center" style={knopf} onClick={onSchliessen}><X size={11} weight="bold" />Schließen</button>
          </div>
        )}
      </div>
    </div>
  );
}
